import React from "react";
import { CSSTransition } from "react-transition-group";
import styled from "@emotion/styled";
import { ReadonlyPreview } from "../ReadonlyPreview";

/**
 * We delay unmounting of the readonly preview overlay to avoid flickering when the Monaco Editor mounts.
 */
export const LAZY_EDITOR_MOUNT_DELAY_MS = 500;

const SReadonlyPreview = styled(ReadonlyPreview)({
  position: "absolute",
  zIndex: 2,

  "&.preview-exit": {
    pointerEvents: "none",
  },
});

const SEditorContainer = styled.div({
  position: "absolute",
  zIndex: 1,
  width: "100%",
  height: "100%",

  "&.editor-enter": {
    opacity: 0,
  },

  "&.editor-enter-active": {
    opacity: 1,
    transition: `opacity ${LAZY_EDITOR_MOUNT_DELAY_MS}ms ease-in-out`,
  },
});

export interface LazyEditorProps {
  defaultValue: string | undefined;
}

const makeLazyEditor = <T extends LazyEditorProps>(EditorComponent: React.ComponentType<T>, language: string) => {
  const LazyEditor = (props: T) => {
    const [isActivated, setIsActivated] = React.useState(false);

    return (
      <>
        <CSSTransition classNames="preview" in={!isActivated} timeout={LAZY_EDITOR_MOUNT_DELAY_MS} unmountOnExit={true}>
          <SReadonlyPreview
            code={props.defaultValue ?? ""}
            language={language}
            onMouseEnter={() => setIsActivated(true)}
          />
        </CSSTransition>

        <CSSTransition classNames="editor" in={isActivated} timeout={LAZY_EDITOR_MOUNT_DELAY_MS}>
          <SEditorContainer>{isActivated ? <EditorComponent {...props} /> : null}</SEditorContainer>
        </CSSTransition>
      </>
    );
  };

  return LazyEditor;
};

export { makeLazyEditor };
